import { readCookie } from "modules/cookieManager";
import { camelCaseObjectKeys } from "utils/camelCase";

/**
 * Retrieves article location parameters from the 'Personal' cookie.
 *
 * @returns {Object} An object containing:
 *   - marketIds {string[]} Array of market IDs (empty if not found).
 *   - marketStateAbbreviations {string[]} Array of market state abbreviations (empty if not found).
 */
const getArticleLocationParameters = function() {
  const personalCookie = readCookie('Personal', true);
  const parameters = new URLSearchParams(personalCookie);
  const marketId = parameters.get('market') || '';
  const marketStateAbbreviation = parameters.get('state') || '';

  return {
    marketIds: marketId ? [marketId] : [],
    marketStateAbbreviations: marketStateAbbreviation ? [marketStateAbbreviation] : [],
  };
};

/**
 * Constructs a blog articles search command object, optionally including location parameters 
 * based on the user's IP address.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.blogArticlesSearchCommand - The base search command for blog articles.
 * @param {boolean} params.includeDataFromUserIpAddress - Whether to include location parameters 
 *        from the user's IP address.
 * @returns {Object} The combined search command object with optional location parameters.
 */
const getBlogArticlesSearchCommand = function({ blogArticlesSearchCommand, includeDataFromUserIpAddress }) {
  return {
    ...blogArticlesSearchCommand,
    ...includeDataFromUserIpAddress ? getArticleLocationParameters() : {}
  };
};

/**
 * Extracts and parses JSON data from the inner text of an article section command element,
 * then converts its keys to camelCase.
 *
 * @param {HTMLElement} articleSectionCommandElement - The DOM element containing the JSON data in its inner text.
 * @returns {Object|null} The parsed object with camelCase keys, or null if no data is found.
 */
const getArticleSectionCommandData = function(articleSectionCommandElement) {
  const articleSectionCommandData = articleSectionCommandElement.innerText || null;
  return articleSectionCommandData ? camelCaseObjectKeys(JSON.parse(articleSectionCommandData)) : null;
};


/**
 * Retrieves and constructs the article section command object from the given element.
 *
 * @param {HTMLElement|null} articleSectionCommandElement - The DOM element containing article section command data.
 * @returns {Object|null} An object containing article section command data and the blog articles search command,
 *                        or null if the element is not provided.
 */
const getArticleSectionCommand = function(articleSectionCommandElement) {
  if(!articleSectionCommandElement){
    return null;
  }

  const articleSectionCommandData = getArticleSectionCommandData(articleSectionCommandElement);
  const blogArticlesSearchCommand = articleSectionCommandData
    ? getBlogArticlesSearchCommand(articleSectionCommandData) : null;

  return articleSectionCommandData ? {
    ...articleSectionCommandData,
    blogArticlesSearchCommand,
  } : null;
};

export default getArticleSectionCommand;